import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useCreateTag, useDeleteTag, useRenameTag, useTags } from '@/hooks/useTags';
import { getTagColorClasses } from '@/lib/tagColors';
import type { Tag } from '@/types';

export function TagManager() {
  const { data: tags = [], isLoading } = useTags();
  const createTag = useCreateTag();
  const renameTag = useRenameTag();
  const deleteTag = useDeleteTag();

  const [newName, setNewName] = useState('');
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const userTags = useMemo(
    () => tags.filter((tag) => tag.source !== 'internal' && tag.source !== 'external'),
    [tags]
  );

  const systemTags = useMemo(
    () => tags.filter((tag) => tag.source === 'internal' || tag.source === 'external'),
    [tags]
  );

  const handleCreate = async () => {
    if (!newName.trim()) return;

    setError(null);
    try {
      await createTag.mutateAsync(newName.trim());
      setNewName('');
    } catch {
      setError('Failed to create tag');
    }
  };

  const startEdit = (tag: Tag) => {
    setEditingTagId(tag.id);
    setEditingName(tag.name);
  };

  const cancelEdit = () => {
    setEditingTagId(null);
    setEditingName('');
  };

  const saveEdit = async () => {
    if (!editingTagId || !editingName.trim()) return;

    setError(null);
    try {
      await renameTag.mutateAsync({ id: editingTagId, name: editingName.trim() });
      cancelEdit();
    } catch {
      setError('Failed to rename tag');
    }
  };

  const handleDelete = async (tagId: string) => {
    setError(null);
    try {
      await deleteTag.mutateAsync(tagId);
    } catch {
      setError('Failed to delete tag');
    }
  };

  const busy =
    isLoading ||
    createTag.isPending ||
    renameTag.isPending ||
    deleteTag.isPending;

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          placeholder="New tag name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={busy}
        />
        <Button onClick={handleCreate} loading={createTag.isPending} disabled={busy || !newName.trim()}>
          Create
        </Button>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Your tags</p>
        {userTags.length === 0 ? (
          <p className="text-sm text-muted-foreground">No custom tags yet.</p>
        ) : (
          <ul className="space-y-2">
            {userTags.map((tag) => {
              const isEditing = editingTagId === tag.id;
              const palette = getTagColorClasses(tag.id || tag.name);

              return (
                <li key={tag.id} className="flex items-center gap-2 rounded border px-2 py-1.5">
                  {isEditing ? (
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      disabled={renameTag.isPending}
                    />
                  ) : (
                    <span
                      className={`inline-flex max-w-[70%] items-center truncate rounded-full border px-2.5 py-0.5 text-xs ${palette.solid}`}
                    >
                      {tag.name}
                    </span>
                  )}

                  {isEditing ? (
                    <>
                      <Button size="sm" onClick={saveEdit} loading={renameTag.isPending}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => startEdit(tag)} disabled={busy}>
                        Rename
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(tag.id)}
                        loading={deleteTag.isPending}
                        disabled={busy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {systemTags.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">System tags</p>
          <div className="flex flex-wrap gap-2">
            {systemTags.map((tag) => {
              const palette = getTagColorClasses(tag.id || tag.name);
              return (
                <span
                  key={tag.id}
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs ${palette.subtle}`}
                >
                  {tag.name}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
